import { useState, useEffect, useRef, useMemo } from "react";
import { filter } from "fuzzaldrin-plus";
import { CountryViewCoordinates } from "./HeatMap";

// API URL is imported from auto-generated config (dev) or replaced at build time (production)
import { API_URL } from "../config/env";

export interface Country {
  name: string;
  region: string;
  iso: string;
  alias: string[];
  coordinates: CountryViewCoordinates | null;
}

interface CountrySelectorProps {
  selectedCountry: Country;
  onCountryChange: (country: Country) => void;
}

// Dropdown minimum width constant
const DROPDOWN_MIN_WIDTH = 320;

export function CountrySelector({
  selectedCountry,
  onCountryChange,
}: CountrySelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [countries, setCountries] = useState<Country[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dropdownAlign, setDropdownAlign] = useState<"left" | "right">("left");
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Fetch countries data from API on mount
  const fetchCountryList = async () => {
    try {
      setIsLoading(true);
      setError(null); // Clear any previous errors
      const response = await fetch(`${API_URL}/countries`);
      if (response.ok) {
        const data = await response.json();
        setCountries(data);
        setFilteredCountries(data);
      } else {
        throw new Error(`API request failed with status ${response.status}.`);
      }
    } catch (error) {
      console.error("Failed to fetch countries data from API:", error);
      setError("Failed to load countries.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCountryList();
  }, []);

  // Memoize combined searchable strings to avoid recreating them on every search query change
  const combinedStrings = useMemo(
    () =>
      countries.map(
        (c) => `${c.name} ${c.iso} ${c.region} ${c.alias.join("; ")}`
      ),
    [countries]
  );

  // Filter countries based on search query
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCountries(countries);
    } else {
      // Use memoized combined strings for fuzzy matching
      const matches = filter(combinedStrings, searchQuery);
      // Use Set for O(1) lookup performance
      const matchSet = new Set(matches);
      const filtered = countries.filter((_, index) =>
        matchSet.has(combinedStrings[index])
      );
      setFilteredCountries(filtered);
    }
  }, [searchQuery, countries, combinedStrings]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate dropdown alignment based on position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Check if there's enough space on the right
      const spaceOnRight = viewportWidth - buttonRect.right;

      // If not enough space on the right, align to the right edge
      // Otherwise, align to the left edge (default)
      if (
        spaceOnRight < DROPDOWN_MIN_WIDTH &&
        buttonRect.left >= DROPDOWN_MIN_WIDTH
      ) {
        setDropdownAlign("right");
      } else {
        setDropdownAlign("left");
      }
    }
  }, [isOpen]);

  const handleCountrySelect = (country: Country) => {
    onCountryChange(country);
    setIsOpen(false);
    setSearchQuery("");
  };

  // Dropdown className with dynamic alignment
  const dropdownClassName = `absolute top-full ${
    dropdownAlign === "right" ? "right-0" : "left-0"
  } mt-2 w-full bg-[#1a1f2e] border border-gray-700 rounded-lg shadow-xl z-50 max-h-[400px] overflow-hidden flex flex-col`;
  const dropdownStyle = { minWidth: `${DROPDOWN_MIN_WIDTH}px` };

  return (
    <div className="relative" ref={containerRef}>
      {/* Selected Country Display / Trigger */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className="flex items-center gap-2 px-4 py-2 bg-[#1a1f2e] border border-gray-700 rounded-lg text-white hover:border-gray-600 transition-colors min-w-[200px] justify-between disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium">
            {isLoading
              ? "Loading countries..."
              : error
              ? error
              : selectedCountry.name}
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && !isLoading && !error && (
        <div className={dropdownClassName} style={dropdownStyle}>
          {/* Search Input */}
          <div className="p-3 border-b border-gray-700">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search countries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-[#0d1117] border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-gray-600"
                autoFocus
              />
            </div>
          </div>

          {/* Countries List */}
          <div className="overflow-y-auto flex-1">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.iso}
                  onClick={() => handleCountrySelect(country)}
                  className={`w-full px-4 py-3 text-left hover:bg-[#161b22] transition-colors border-b border-gray-800 last:border-b-0 ${
                    country.name === selectedCountry.name ? "bg-[#161b22]" : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">
                        {country.name}
                      </div>
                      <div className="text-gray-400 text-sm">
                        {country.region} • {country.iso}
                      </div>
                    </div>
                    {country.name === selectedCountry.name && (
                      <svg
                        className="w-5 h-5 text-green-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-400">
                No countries found matching "{searchQuery}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
